import { Feather } from "lucide-react";

// 밋밋한 회색 박스 하나짜리 스켈레톤 대신, 실제 콘텐츠 모양(차트/표)을 흉내낸 반짝임
// 스켈레톤(2026-07-17 피드백 — "스켈레톤이 좀 자연스러운 UX 애니메이션이면 좋겠다").
// 막대마다 animationDelay 를 살짝 어긋나게 줘서 전부 똑같이 깜빡이지 않고 옆으로 흐르듯
// "꿈틀꿈틀" 움직이는 느낌을 준다.
//
// 브랜드 로더(2026-07-18 피드백 — "트렌디하게, 브랜드에 맞게, 펜이 날아다니듯") —
// 언론사 서비스답게 만년필이 잉크 선을 그리며 날아가는 모션을 차트 자리 중앙에 얹는다.
// 셔머도 회색 → 옅은 블루 틴트(tailwind.config shimmer)로 교체.
function PenLoader() {
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2">
      <div className="relative h-10 w-44">
        <svg viewBox="0 0 176 40" className="absolute inset-0 h-full w-full">
          <path
            d="M8 30 C 34 20, 52 34, 78 27 S 128 18, 168 25"
            fill="none"
            stroke="#3B82F6"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="ink-draw"
          />
        </svg>
        <Feather className="pen-fly absolute left-1 top-0 h-5 w-5 text-brand-600" />
      </div>
      <span className="text-xs text-ink-400">
        기자님들의 기록을 모아오고 있어요
        <span className="dot-pulse">·</span>
        <span className="dot-pulse" style={{ animationDelay: "0.2s" }}>·</span>
        <span className="dot-pulse" style={{ animationDelay: "0.4s" }}>·</span>
      </span>
    </div>
  );
}
function Bar({ className = "", style = {}, delay = 0 }) {
  return (
    <div
      className={`animate-shimmer bg-shimmer bg-[length:200%_100%] ${className}`}
      style={{ animationDelay: `${delay}ms`, ...style }}
    />
  );
}

// 실제 라인 차트를 흉내낸 막대 실루엣 — 굴곡이 있어야 "차트"처럼 보여서 고정된 높이
// 패턴을 하나 둔다(매 렌더마다 랜덤이면 오히려 산만해서 랜덤은 안 씀).
const CHART_BAR_HEIGHTS = [35, 55, 45, 70, 50, 82, 60, 68, 42, 78, 52, 65, 48, 88, 58];

function ChartSkeleton({ height }) {
  return (
    <div className="relative flex items-end gap-1.5 overflow-hidden rounded-lg border border-ink-100 bg-white p-3" style={{ height }}>
      {CHART_BAR_HEIGHTS.map((pct, i) => (
        <Bar key={i} className="flex-1 rounded-t-sm opacity-60" style={{ height: `${pct}%` }} delay={i * 50} />
      ))}
      <PenLoader />
    </div>
  );
}

function TableRowSkeleton({ delay }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Bar className="h-3 w-14 rounded" delay={delay} />
      <Bar className="h-3 flex-[2] rounded" delay={delay + 40} />
      <Bar className="h-3 flex-1 rounded" delay={delay + 80} />
      <Bar className="h-3 flex-1 rounded" delay={delay + 120} />
      <Bar className="h-3 flex-1 rounded" delay={delay + 160} />
    </div>
  );
}

function TableSkeleton({ rows }) {
  return (
    <div className="divide-y divide-ink-100 rounded-lg border border-ink-200 bg-white">
      {Array.from({ length: rows }, (_, i) => (
        <TableRowSkeleton key={i} delay={i * 70} />
      ))}
    </div>
  );
}

// variant: "chart"(차트만) | "table"(표만) | "chartTable"(차트+표, 기본값).
// h 는 기존 호출부와 호환되는 단위(×4px, 예전 flat box 높이 그대로 유지) — chartTable은
// 그중 55%를 차트에, 나머지를 표 행 개수 추정에 쓴다.
export default function Skeleton({ h = 72, variant = "chartTable" }) {
  const totalHeight = h * 4;
  if (variant === "chart") {
    return <ChartSkeleton height={totalHeight} />;
  }
  if (variant === "table") {
    return <TableSkeleton rows={Math.max(4, Math.round(totalHeight / 44))} />;
  }
  return (
    <div className="space-y-4">
      <ChartSkeleton height={Math.round(totalHeight * 0.55)} />
      <TableSkeleton rows={4} />
    </div>
  );
}
